import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SquareWarnIcon,
  UpgradeWarnIcon,
  CheckLinearIcon
} from '@/components/common/icon';
import {
  getPlatformVideoContentType,
  getPlatformVideoContentTypeDesc
} from '@/biz/platform';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { OrderStatus } from '@/interface/order';
import Radio from '@/components/Radio/index';
import { ProductType, ProductPhysicalSelectEnum } from '../config';
import axiosInstance from '../../../apis/axiosInstance';
import { isMobile } from '@/biz/tool';
import { Switch, Select } from 'aha-components';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { NationsLab } from '@/utils/locations';
import { buildCountrySelectOptions, getCountryName } from '@/utils/locations';
import ShareRateInput from './ShareRateInput';
import AhaPicksPrePriceFields from './AhaPicksPrePriceFields';
import ScreeningQuestions from './ScreeningQuestions';
import CreatorScheduleForm from './CreatorScheduleForm';
import { hasSubmittedScheduleConflict } from '@/utils/creatorScheduleConflict';
const PriceFieldsComponent = ({
  direction = 'flex flex-col',
  hasRadioButton = false,
  orderPriceAdjustment = false,
  newOrderPrice,
  setNewOrderPrice,
  nonNegotiable = false,
  setNonNegotiable = () => {},
  isModal = false,
  onPriceRangeValidChange,
  selectCountryError,
  setSelectCountryError
}: {
  direction?: 'flex flex-row' | 'flex flex-col';
  hasRadioButton?: boolean;
  orderPriceAdjustment?: boolean;
  newOrderPrice?: number | string;
  setNewOrderPrice?: (v: number | string) => void;
  nonNegotiable?: boolean;
  setNonNegotiable?: (v: boolean) => void;
  isModal?: boolean;
  onPriceRangeValidChange?: (isValid: boolean) => void;
  selectCountryError?: boolean;
  setSelectCountryError?: (v: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { gbPricingSchemaFlag } = useMultiplePricesStore();
  const {
    setShowModalShowImage,
    showModalAcceptAhaPicksOffer,
    setAgreeProductShip,
    productShippingCountry,
    setProductShippingCountry
  } = useWebModalStore();
  const { order, campaign, product } = detailData;
  const priceFields = order?.priceFields;
  const interestedPriceFields = order?.interestedPriceFields;
  const scheduleConflictSubmitted = hasSubmittedScheduleConflict(
    order?.scheduleConflict
  );

  const locations = detailData?.campaign?.locations || [];
  const countries = locations
    ?.map((item: string[]) => NationsLab?.[item?.[1]])
    ?.join(', ');

  const campaignCountries = locations
    ?.map((item: string[]) => item?.[1])
    ?.join(', ');

  const isProductShip =
    product?.productType === ProductType.PHYSICAL_PRODUCT &&
    product?.physicalProductShipType === ProductPhysicalSelectEnum.ship;

  const { selectedPriceFields } = useMultiplePricesStore();

  // 先请求下改价范围
  const getSetPriceRange = async (selectedData?: any) => {
    const interestedPriceFields = selectedData || selectedPriceFields;
    if (interestedPriceFields?.length === 0) {
      return;
    }
    const params = {
      interestedPriceField: {
        ...interestedPriceFields[0]
      }
    };
    const { data } = await axiosInstance.post(
      `/order/${detailData?.order?.id}/competitivePrice`,
      params
    );
    const interestedPriceField = data?.data?.interestedPriceField;
    useMultiplePricesStore.setState({
      creatorLowActualGet:
        interestedPriceField?.fairPriceSuggestion?.creatorLowActualGet ?? null,
      creatorHighActualGet:
        interestedPriceField?.fairPriceSuggestion?.creatorHighActualGet ?? null,
      creatorMaxActualGet: interestedPriceField?.creatorMaxActualGet ?? null
    });
  };

  useEffect(() => {
    getSetPriceRange();
  }, []);

  if (!gbPricingSchemaFlag) {
    // 暂未拿到 ab 测试结果
    return (
      <div className="w-full flex justify-center items-center gap-8 ">
        {t('price.fields.loading', { defaultValue: 'loading price...' })}
      </div>
    );
  }
  // no.1 老订单
  // old 单一内容类型定价
  if (!priceFields?.length) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-8 ">
        <PriceFieldItem
          priceData={{
            price: order?.price,
            contentType: getPlatformVideoContentType({
              platform: order?.platform
            })
          }}
          alwaysShowPrice
          hasRadioButton={hasRadioButton}
        />
        {order?.status.includes(OrderStatus.Invited) && (
          <p>
            {t('price.fields.abnormal_order', {
              defaultValue: 'The order is abnormal, please contact'
            })}{' '}
            <a
              className="text-[#0c67e6] cursor-pointer"
              href="mailto:support@creator.ahacreator.com"
            >
              support@creator.ahacreator.com
            </a>
            .
          </p>
        )}
      </div>
    );
  }

  // no.2 新订单，已提交价格
  if (interestedPriceFields?.length > 0) {
    return (
      <div className="w-full justify-center items-center gap-4 ">
        {interestedPriceFields?.map((el: any, index: number) => {
          return (
            <React.Fragment key={`${el.contentType}-${index}`}>
              <PriceFieldItem
                priceData={el}
                alwaysShowPrice
                hasRadioButton={hasRadioButton}
              />
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // no.3 新订单，未提交价格，gbPricingSchemaFlag是 not_display_price_can_change
  if (priceFields?.length && !interestedPriceFields?.length) {
    // 加入前 不展示定价
    if (gbPricingSchemaFlag === 'not_display_price_can_change') {
      return (
        <div className="w-full flex justify-center items-center gap-8 ">
          <span>
            {t('price.fields.open_budget', { defaultValue: 'Open budget' })}
          </span>
        </div>
      );
    }
  }

  // no.4 新订单，未提交价格
  return (
    <>
      {hasRadioButton &&
        detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
        detailData?.product?.physicalProductShipType ===
          ProductPhysicalSelectEnum.ship && (
          <div className="px-4 py-2 bg-[#EFF6FF] rounded-lg mb-3 flex items-start gap-2">
            <div className="w-6 h-6">
              <SquareWarnIcon className="w-6 h-6" />
            </div>
            <div
              className={`flex ${isMobile() ? 'flex-col justify-start items-start' : 'items-center'}`}
            >
              <span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('price.fields.receiving_product', {
                      defaultValue:
                        'You’ll receive a free product (worth <b>${{price}}</b>) as part of this collaboration. Many creators consider this when setting their price',
                      price: detailData?.product?.physicalProductCashValue
                    })
                  }}
                ></span>
                <span>{'.'}</span>
              </span>
            </div>
          </div>
        )}

      {!!order?.ahaPicksOffer ? (
        <AhaPicksPrePriceFields
          order={order}
          className={isModal ? '!flex-row px-[1px]' : ''}
          getSetPriceRange={getSetPriceRange}
        />
      ) : (
        <div
          className={`w-full justify-center items-stretch gap-4
        ${direction}
        `}
        >
          {priceFields?.map((el: any, index: number) => {
            return (
              <React.Fragment key={`${el.contentType}-${index}`}>
                <PriceFieldItem
                  priceData={el}
                  alwaysShowPrice
                  hasRadioButton={hasRadioButton}
                  getSetPriceRange={getSetPriceRange}
                />
              </React.Fragment>
            );
          })}
        </div>
      )}
      {orderPriceAdjustment && (
        <>
          <div className="flex flex-col gap-4">
            <ShareRateInput
              setNewOrderPrice={
                setNewOrderPrice as (price: number | string) => void
              }
              newOrderPrice={newOrderPrice as number}
              disabled={showModalAcceptAhaPicksOffer}
              isAcceptOffer={showModalAcceptAhaPicksOffer}
              lastAdsOfferAmount={
                !!order?.ahaPicksOffer
                  ? selectedPriceFields?.[0]?.actualGet
                  : null
              }
              isCounterOffer={
                !!order?.ahaPicksOffer && !showModalAcceptAhaPicksOffer
              }
              onPriceRangeValidChange={onPriceRangeValidChange}
            />
            <div className="w-full h-24 bg-gradient-to-b from-gray-100 to-gray-100/0 rounded-2xl px-[10px] py-2">
              <div className="border-2 border-[#E5E7EB] rounded-lg h-full w-full flex justify-between items-center p-[14px]">
                <div className="flex flex-col gap-2">
                  <div className="text-[#292524] text-sm font-semibold leading-5">
                    {t('price.fields.set_your_rate.fixed_rate', {
                      defaultValue: 'Make this rate fixed (non-negotiable)'
                    })}
                  </div>
                  <div className="text-[#57534D] text-xs font-normal leading-4">
                    {t('price.fields.set_your_rate.fixed_rate_desc', {
                      defaultValue:
                        'Once turned on, the brand can only accept or decline.'
                    })}
                  </div>
                </div>
                <div>
                  <Switch
                    className={`${!nonNegotiable ? '!bg-[#D1D1DB]' : ''}`}
                    checked={nonNegotiable}
                    onChange={(checked) => setNonNegotiable?.(checked)}
                    disabled={showModalAcceptAhaPicksOffer}
                  />
                </div>
              </div>
            </div>
            <ScreeningQuestions />
            {!scheduleConflictSubmitted && <CreatorScheduleForm />}
            {isProductShip && (
              <div id="take-order-shipping-country" className="mt-4 mb-2">
                <div className="text-[#0D0D0D] text-base font-semibold leading-6 mb-2">
                  {t('interest.shipping_country.title', {
                    defaultValue: 'Product shipping country'
                  })}
                  <span className="text-red-500"> *</span>
                </div>
                <Select
                  error={selectCountryError}
                  showSearch
                  value={productShippingCountry || undefined}
                  onChange={(value) => {
                    if (selectCountryError) {
                      setSelectCountryError?.(false);
                    }
                    const selected = (value as string) || '';
                    setProductShippingCountry(selected);
                    setAgreeProductShip(campaignCountries?.includes(value)); // 判断地区是否符合
                  }}
                  options={buildCountrySelectOptions()}
                  placeholder={t('interest.shipping_country.please_select', {
                    defaultValue: 'Please select'
                  })}
                  // 因为移动端的层级是9999，所以需要设置一个更高的层级
                  dropdownStyle={{ zIndex: 10000 }}
                />
                {selectCountryError ? (
                  <div className="text-red-500 text-sm mt-2">
                    {productShippingCountry
                      ? t('campaign.modal.submit_interest.location_notice', {
                          defaultValue: 'Please confirm locations.'
                        })
                      : t('interest.shipping_country.please_select_country', {
                          defaultValue: 'This is a required field.'
                        })}
                  </div>
                ) : null}
                {productShippingCountry ? (
                  campaignCountries?.includes(productShippingCountry) ? (
                    <div className="flex items-center gap-2 mt-2 text-sm font-medium text-[#079455]">
                      <CheckLinearIcon className="w-6 h-6 text-[#079455]" />
                      <span>
                        {t('interest.shipping_country.description', {
                          defaultValue: 'Brand can ship to {{country}}',
                          country: getCountryName(productShippingCountry)
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="bg-[#FEF2F2] leading-5 px-4 py-3 rounded-lg mt-2">
                      <div className="flex items-start gap-2">
                        <div className="mt-[2px]">
                          <UpgradeWarnIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-[#292524] text-base font-semibold">
                            {t('interest.shipping_country.cannot_ship_title', {
                              defaultValue: 'Brand cannot ship to {{country}}',
                              country: getCountryName(productShippingCountry)
                            })}
                          </div>
                          <div className="text-[#292524] text-sm font-normal mt-2">
                            {t(
                              'interest.shipping_country.cannot_ship_description',
                              {
                                defaultValue:
                                  'This brand only ships to: {{countries}}. If you can receive products in any of these locations, please update your country selection.',
                                countries: countries
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

const PriceFieldItem = ({
  priceData,
  alwaysShowPrice,
  hasRadioButton = false,
  getSetPriceRange
}: {
  priceData: any;
  alwaysShowPrice?: boolean;
  hasRadioButton?: boolean;
  getSetPriceRange?: (selectedData?: any) => void;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { order, campaign } = detailData;
  const interestedPriceFields = order?.interestedPriceFields;

  const { selectedPriceFields, setSelectedPriceFields, gbPricingSchemaFlag } =
    useMultiplePricesStore();
  const { contentType, price, originPrice, actualGet } = priceData;
  let showPrice = actualGet;

  return (
    <div
      className={`gap-1 flex flex-col justify-start items-start border-2 border-solid rounded-lg px-4 py-3 w-full
      ${
        selectedPriceFields?.find((el) => el?.contentType === contentType) ||
        interestedPriceFields?.find(
          (el: any) => el?.contentType === contentType
        )
          ? 'border-theme-color'
          : 'border-[#e8e8ec]'
      }
          ${
            interestedPriceFields?.length > 0 ||
            !order?.status.includes(OrderStatus.Invited)
              ? 'cursor-default'
              : 'cursor-pointer'
          }
      `}
      onClick={() => {
        if (
          interestedPriceFields?.length > 0 ||
          !order?.status.includes(OrderStatus.Invited)
        ) {
          return;
        }
        setSelectedPriceFields([priceData]);
        getSetPriceRange?.([priceData]);
      }}
    >
      <div className="flex justify-between w-full">
        <div
          className={`flex justify-between w-full flex-col items-start gap-2
        `}
        >
          <div
            className={`text-[#1C1917] text-[12px] leading-snug font-semibold`}
          >
            {getPlatformVideoContentType({
              platform: order?.platform,
              contentType: contentType
            })}
          </div>
        </div>
        {hasRadioButton && (
          <Radio
            checked={Boolean(
              selectedPriceFields?.find((el) => el?.contentType === contentType)
            )}
          />
        )}
      </div>
      <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
        {getPlatformVideoContentTypeDesc(
          getPlatformVideoContentType({
            platform: order?.platform,
            contentType: contentType
          }),
          order?.platform
        )}
      </div>
    </div>
  );
};

export default PriceFieldsComponent;
